import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Shield, Users, Sparkles, HeartHandshake } from 'lucide-react';

/**
 * Landing pública /granada
 *
 * Objetivos:
 *  - Carga ligera móvil-first.
 *  - Tono sobrio, marca GUARDIANSFIT, sin lenguaje personalista.
 *  - Único CTA: prueba de 14 días con tag "feria-granada".
 */
const GranadaLandingPage = () => {
  const navigate = useNavigate();
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    document.title = 'GUARDIANSFIT · Estructura tu entrenamiento';
    return () => { document.title = 'GuardiansFit'; };
  }, []);

  const goToRegister = (tier) => {
    navigate(`/register?origen=feria-granada&tier=${tier}`);
  };

  return (
    <div
      data-testid="granada-landing"
      className="min-h-screen w-full text-white overflow-x-hidden"
      style={{
        background:
          'radial-gradient(circle at 20% -10%, rgba(34,197,94,0.18) 0%, rgba(10,15,12,0) 45%), radial-gradient(circle at 90% 100%, rgba(34,197,94,0.10) 0%, rgba(10,15,12,0) 45%), #0a0f0c',
      }}
    >
      <div className="max-w-md mx-auto px-6 pt-10 pb-6 sm:pt-14">
        {/* Logo / marca */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <Shield className="h-5 w-5 text-emerald-400" strokeWidth={2.5} />
          <span
            data-testid="granada-brand"
            className="text-sm tracking-[0.32em] font-semibold text-emerald-300 uppercase"
          >
            GUARDIANSFIT
          </span>
        </div>

        {/* Foto entrenador */}
        <div className="relative mx-auto w-52 h-52 sm:w-60 sm:h-60 rounded-full overflow-hidden mb-8 ring-4 ring-emerald-500/30 shadow-[0_0_60px_-15px_rgba(34,197,94,0.5)]">
          {!imgLoaded && (
            <div className="absolute inset-0 bg-neutral-900 animate-pulse" />
          )}
          <img
            src="/granada/jesus-clean-fade.jpg"
            alt="Entrenador de GUARDIANSFIT"
            data-testid="granada-photo"
            className="w-full h-full object-cover"
            onLoad={() => setImgLoaded(true)}
          />
          <div className="absolute inset-0 rounded-full ring-1 ring-white/10 pointer-events-none" />
        </div>

        {/* Etiqueta + Nombre */}
        <div className="text-center mb-1">
          <p className="text-emerald-300/90 text-xs uppercase tracking-widest">TU ENTRENADOR</p>
          <h2 className="text-2xl font-bold mt-1">Jesús — Guardián</h2>
        </div>

        {/* Titular */}
        <h1 className="text-3xl sm:text-4xl font-bold text-center leading-tight mt-8">
          Estructura tu entrenamiento.
          <br />
          <span className="text-emerald-400">Cuerpo, mente y propósito.</span>
        </h1>

        {/* Sub-titular */}
        <p className="text-neutral-300 text-center mt-4 leading-relaxed">
          Entrenamiento estructurado, seguimiento real y un método probado para
          avanzar con orden. <strong className="text-white">Sin humo.</strong>
        </p>

        {/* Bullets */}
        <div className="mt-8 space-y-3">
          {[
            'Plan estructurado para avanzar paso a paso',
            'Vídeos explicativos para cada ejercicio',
            'Seguimiento real y revisión de tu progreso',
          ].map((b) => (
            <div key={b} className="flex items-start gap-3">
              <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
                <Check className="h-3 w-3 text-emerald-300" strokeWidth={3} />
              </div>
              <p className="text-neutral-200 text-sm">{b}</p>
            </div>
          ))}
        </div>

        {/* CTA principal */}
        <button
          data-testid="granada-cta-primary"
          onClick={() => goToRegister('client_pro_month')}
          className="group mt-10 w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-bold py-4 rounded-2xl text-lg shadow-[0_10px_30px_-10px_rgba(34,197,94,0.6)] transition-all duration-200 hover:scale-[1.02]"
        >
          Empieza tu prueba de 14 días
          <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" strokeWidth={2.5} />
        </button>

        {/* Micro-textos bajo CTA */}
        <p className="text-center text-xs text-neutral-400 mt-3">
          Sin tarjeta obligatoria · Cancela cuando quieras
        </p>

        {/* Planes */}
        <div className="mt-10 space-y-3">
          <p className="text-xs uppercase tracking-widest text-neutral-500 text-center mb-2">
            Elige cómo empezar
          </p>

          <button
            data-testid="granada-tier-free"
            onClick={() => goToRegister('free')}
            className="w-full text-left rounded-xl bg-white/5 border border-white/10 px-4 py-3 hover:border-emerald-500/40 transition"
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="font-semibold">Explorar</p>
                <p className="text-xs text-neutral-400">Acceso inicial a la plataforma</p>
              </div>
              <span className="text-emerald-400 font-bold">0€</span>
            </div>
          </button>

          <button
            data-testid="granada-tier-pro-month"
            onClick={() => goToRegister('client_pro_month')}
            className="w-full text-left rounded-xl bg-emerald-500/10 border border-emerald-400/50 px-4 py-3 hover:bg-emerald-500/20 transition ring-1 ring-emerald-400/20"
          >
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold">Mensual</p>
                  <span className="text-[10px] bg-emerald-400 text-neutral-950 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide">
                    Recomendado
                  </span>
                </div>
                <p className="text-xs text-neutral-300">14 días gratis · acceso completo</p>
              </div>
              <span className="text-white font-bold text-lg">9,99€/mes</span>
            </div>
          </button>

          <button
            data-testid="granada-tier-pro-year"
            onClick={() => goToRegister('client_pro_year')}
            className="w-full text-left rounded-xl bg-white/5 border border-white/10 px-4 py-3 hover:border-emerald-500/40 transition"
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="font-semibold">Anual</p>
                <p className="text-xs text-neutral-400">Ahorra frente al plan mensual</p>
              </div>
              <span className="text-white font-bold">79€/año</span>
            </div>
          </button>
        </div>

        {/* Sellos de confianza */}
        <div className="mt-10 flex items-center justify-center gap-6 text-xs text-neutral-400">
          <div className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />
            <span>Clientes reales</span>
          </div>
          <div className="w-px h-3 bg-neutral-700" />
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5" />
            <span>Método probado</span>
          </div>
          <div className="w-px h-3 bg-neutral-700" />
          <div className="flex items-center gap-1.5">
            <HeartHandshake className="h-3.5 w-3.5" />
            <span>Seguimiento humano</span>
          </div>
        </div>

        {/* Footer minimal */}
        <div className="mt-10 pb-8 text-center text-[11px] text-neutral-600">
          <p>© GUARDIANSFIT · Salud estructurada para todos</p>
          <div className="mt-1 flex items-center justify-center gap-3">
            <a href="/legal/privacidad" className="hover:text-neutral-400">Privacidad</a>
            <span>·</span>
            <a href="/legal/terminos-uso" className="hover:text-neutral-400">Términos</a>
            <span>·</span>
            <a href="/legal/aviso-legal" className="hover:text-neutral-400">Aviso legal</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GranadaLandingPage;
